import { Header } from "@/components/Header"
import { PropertyCard } from "@/components/PropertyCard"
import { SearchBar } from "@/components/SearchBar"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useTrackPageView } from "@/hooks/use-track-page-view"
import heroImage from "@assets/generated_images/Manila_skyline_hero_background_049aac5a.webp"
import type { Property } from "@shared/schema"
import { useQuery } from "@tanstack/react-query"
import { CheckCircle, Clock, Home, Shield, TrendingUp } from "lucide-react"
import { Suspense, useMemo } from "react"
import { Link } from "wouter"
export default function HomePage() {
  useTrackPageView({ page: "home" })
  // Fetch featured properties
  const { data: featuredProperties, isLoading } = useQuery<Property[]>({
    queryKey: ["/api/properties/featured"],
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Randomize properties on each page load (memoized so it doesn't change during re-renders)
  const randomizedProperties = useMemo(() => {
    if (!featuredProperties || featuredProperties.length === 0) return []

    // Shuffle array using Fisher-Yates algorithm
    const shuffled = [...featuredProperties]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }

    // Return up to 6 random properties
    return shuffled.slice(0, 6)
  }, [featuredProperties])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <Header />

      {/* Hero Section - Performance Optimized with Responsive Images */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* PERFORMANCE: Responsive hero image with multiple sizes */}
        <picture>
          <source
            media="(max-width: 640px)"
            srcSet={heroImage}
            width="640"
            height="360"
          />
          <source
            media="(max-width: 1024px)"
            srcSet={heroImage}
            width="1024"
            height="576"
          />
          <img
            src={heroImage}
            alt="Houses for sale and condos for rent in Manila Philippines"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            width="1920"
            height="1080"
          />
        </picture>

        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70" />

        <div className="relative z-10 container max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
            <p className="text-white/90 text-sm font-medium">
              🏆 Philippines' Leading Property Marketplace
            </p>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Discover Your Perfect Home
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Across the Philippines
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Browse thousands of verified properties in Manila, Cebu, Davao, and
            beyond. From luxury condos to affordable homes—find it all in one
            place.
          </p>

          <div className="max-w-4xl mx-auto mb-10">
            <SearchBar />
          </div>

          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="h-14 px-10 text-base font-semibold bg-white text-gray-900 hover:bg-gray-100 shadow-xl"
              asChild
            >
              <Link href="/browse" data-testid="button-browse-properties">
                Browse Properties
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-14 px-10 text-base font-semibold backdrop-blur-md bg-white/10 border-white/30 text-white hover:bg-white/20"
              asChild
              aria-label="List your property for sale or rent"
            >
              <Link
                href="/post-property"
                data-testid="button-post-property-hero"
              >
                List Your Property
              </Link>
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap gap-8 justify-center text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" aria-hidden="true" />
              <span>10,000+ Verified Listings</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" aria-hidden="true" />
              <span>Secure Transactions</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" aria-hidden="true" />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Professional Layout */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Your Trusted Real Estate Partner
            </h2>
            <div
              className="w-20 h-1 bg-blue-600 dark:bg-blue-500 mx-auto rounded-full"
              aria-hidden="true"
            />
          </div>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
              <strong>Find your dream property</strong> with PH Property Finder,
              the Philippines' most trusted real estate marketplace. Whether
              you're looking for <em>houses for sale in Manila</em>,{" "}
              <strong>condos for rent in Makati</strong>, or apartments in Cebu
              and Davao, we connect you with thousands of verified property
              listings from trusted sellers and licensed real estate agents
              across the country.
            </p>

            <p className="text-base text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
              Our extensive database features a wide range of properties
              including affordable housing, luxury condos in BGC, townhouses in
              Quezon City, foreclosed properties, preselling condos,
              ready-for-occupancy units, and commercial real estate. Each
              listing includes detailed high-resolution photos, accurate floor
              plans, transparent pricing information, and direct contact
              details.
            </p>

            <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
              Join over 5,000 satisfied clients who have successfully found
              their perfect home through our platform. Start your property
              search today and discover why we're the preferred choice for
              buying, selling, and renting properties in the Philippines.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Properties Section - Randomized on Each Page Load */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="container max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-3 px-4 py-1.5 bg-blue-50 dark:bg-blue-950 rounded-full">
              <p className="text-blue-600 dark:text-blue-400 text-sm font-semibold uppercase tracking-wide">
                Featured
              </p>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Premium Properties
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
              Discover our hand-picked selection of exceptional properties in
              prime Philippine locations
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <Card
                  key={i}
                  className="h-96 animate-pulse bg-gray-100 dark:bg-gray-800 border-0 shadow-lg"
                  aria-label="Loading property"
                />
              ))}
            </div>
          ) : randomizedProperties && randomizedProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {randomizedProperties.map((property, index) => (
                <article
                  key={property.id}
                  className="transform transition-all duration-300 hover:-translate-y-2"
                >
                  <PropertyCard property={property} priority={index < 3} />
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-2xl">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                No featured properties available at the moment.
              </p>
            </div>
          )}

          <div className="text-center mt-16">
            <Button
              size="lg"
              className="h-14 px-10 text-base font-semibold shadow-lg"
              asChild
            >
              <Link href="/browse" data-testid="button-view-all-properties">
                View All Properties
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* PERFORMANCE: Lazy load below-the-fold sections */}
      <Suspense fallback={<div className="h-screen" />}>
        {/* Properties by Location - Card Grid */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
          <div className="container max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-16">
              <div className="inline-block mb-3 px-4 py-1.5 bg-green-50 dark:bg-green-950 rounded-full">
                <p className="text-green-700 dark:text-green-400 text-sm font-semibold uppercase tracking-wide">
                  Explore
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Browse by Location
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                Find your ideal property in the Philippines' most sought-after
                cities
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white dark:bg-gray-800">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                    <Home
                      className="h-6 w-6 text-blue-600 dark:text-blue-400"
                      aria-hidden="true"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Manila
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    Discover homes and condos in Metro Manila's premier
                    districts including Makati, BGC, Quezon City, and Taguig.
                  </p>
                  <Button
                    variant="ghost"
                    className="p-0 h-auto font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 group"
                    asChild
                    aria-label="Explore properties in Manila"
                  >
                    <Link href="/browse?location=Manila">Explore Manila →</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white dark:bg-gray-800">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-6">
                    <Home
                      className="h-6 w-6 text-green-600 dark:text-green-400"
                      aria-hidden="true"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Cebu
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    Browse condos and apartments in Cebu City's thriving
                    business districts and coastal communities.
                  </p>
                  <Button
                    variant="ghost"
                    className="p-0 h-auto font-semibold text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                    asChild
                    aria-label="Explore properties in Cebu"
                  >
                    <Link href="/browse?location=Cebu">Explore Cebu →</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white dark:bg-gray-800">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-6">
                    <Home
                      className="h-6 w-6 text-purple-600 dark:text-purple-400"
                      aria-hidden="true"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Davao
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    Find affordable apartments and rental properties in
                    Mindanao's economic hub and surrounding areas.
                  </p>
                  <Button
                    variant="ghost"
                    className="p-0 h-auto font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                    asChild
                    aria-label="Explore properties in Davao"
                  >
                    <Link href="/browse?location=Davao">Explore Davao →</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works - Professional Design */}
        <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
          <div className="container max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-16">
              <div className="inline-block mb-3 px-4 py-1.5 bg-orange-50 dark:bg-orange-950 rounded-full">
                <p className="text-orange-700 dark:text-orange-400 text-sm font-semibold uppercase tracking-wide">
                  Process
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                How It Works
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                Your journey to finding the perfect property in three simple
                steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center relative">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white text-2xl font-bold mb-6 shadow-xl"
                  aria-hidden="true"
                >
                  1
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Search & Filter
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Use our advanced search tools to filter thousands of verified
                  listings by location, price, property type, and amenities.
                </p>
              </div>

              <div className="text-center relative">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 text-white text-2xl font-bold mb-6 shadow-xl"
                  aria-hidden="true"
                >
                  2
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  View Details
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Explore high-quality photos, floor plans, virtual tours, and
                  comprehensive property information for informed decisions.
                </p>
              </div>

              <div className="text-center relative">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 text-white text-2xl font-bold mb-6 shadow-xl"
                  aria-hidden="true"
                >
                  3
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Connect & Close
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Message owners directly, schedule viewings, and complete your
                  transaction with confidence and security.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section - Modern Design */}
        <section
          className="py-20 md:py-28 bg-gradient-to-br from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900 text-white"
          aria-label="Statistics"
        >
          <div className="container max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-3">
                  10,000+
                </div>
                <p className="text-blue-100 dark:text-blue-200 text-lg font-medium">
                  Active Property Listings
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-3">
                  5,000+
                </div>
                <p className="text-blue-100 dark:text-blue-200 text-lg font-medium">
                  Happy Clients
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-3">50+</div>
                <p className="text-blue-100 dark:text-blue-200 text-lg font-medium">
                  Cities Covered
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Enhanced */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
          <div className="container max-w-4xl mx-auto px-4 md:px-8 text-center">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 rounded-3xl p-12 md:p-16 shadow-2xl">
              <TrendingUp
                className="h-16 w-16 mx-auto mb-6 text-white"
                aria-hidden="true"
              />
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                Ready to Find Your Dream Home?
              </h2>
              <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                Join thousands of satisfied clients and start your property
                search today with the Philippines' most trusted real estate
                platform.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  size="lg"
                  className="h-14 px-10 text-base font-semibold bg-white text-gray-900 hover:bg-gray-100 shadow-xl"
                  asChild
                >
                  <Link href="/register" data-testid="button-get-started">
                    Get Started Free
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-10 text-base font-semibold border-2 border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link href="/browse" data-testid="button-explore-listings">
                    Explore Listings
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer - Professional Layout */}
        <footer className="bg-gray-900 dark:bg-black text-gray-300 dark:text-gray-400 py-16">
          <div className="container max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
              {/* Property Types */}
              <div>
                <h3 className="text-white font-bold text-lg mb-6">
                  Property Types
                </h3>
                <nav
                  className="flex flex-col space-y-3"
                  aria-label="Property types"
                >
                  <Link
                    href="/houses-for-sale"
                    className="hover:text-white transition-colors"
                  >
                    Houses for Sale
                  </Link>
                  <Link
                    href="/condos-for-rent"
                    className="hover:text-white transition-colors"
                  >
                    Condos for Rent
                  </Link>
                  <Link
                    href="/apartments"
                    className="hover:text-white transition-colors"
                  >
                    Apartments
                  </Link>
                  <Link
                    href="/townhouses"
                    className="hover:text-white transition-colors"
                  >
                    Townhouses
                  </Link>
                  <Link
                    href="/lots-for-sale"
                    className="hover:text-white transition-colors"
                  >
                    Lots for Sale
                  </Link>
                </nav>
              </div>

              {/* Popular Locations */}
              <div>
                <h3 className="text-white font-bold text-lg mb-6">
                  Popular Locations
                </h3>
                <nav
                  className="flex flex-col space-y-3"
                  aria-label="Popular locations"
                >
                  <Link
                    href="/manila-properties"
                    className="hover:text-white transition-colors"
                  >
                    Manila Properties
                  </Link>
                  <Link
                    href="/makati-condos"
                    className="hover:text-white transition-colors"
                  >
                    Makati Condos
                  </Link>
                  <Link
                    href="/bgc-apartments"
                    className="hover:text-white transition-colors"
                  >
                    BGC Apartments
                  </Link>
                  <Link
                    href="/cebu-properties"
                    className="hover:text-white transition-colors"
                  >
                    Cebu Properties
                  </Link>
                  <Link
                    href="/davao-real-estate"
                    className="hover:text-white transition-colors"
                  >
                    Davao Real Estate
                  </Link>
                </nav>
              </div>

              {/* Special Offers */}
              <div>
                <h3 className="text-white font-bold text-lg mb-6">
                  Special Offers
                </h3>
                <nav
                  className="flex flex-col space-y-3"
                  aria-label="Special offers"
                >
                  <Link
                    href="/foreclosed-properties"
                    className="hover:text-white transition-colors"
                  >
                    Foreclosed Properties
                  </Link>
                  <Link
                    href="/preselling-condos"
                    className="hover:text-white transition-colors"
                  >
                    Preselling Condos
                  </Link>
                  <Link
                    href="/commercial"
                    className="hover:text-white transition-colors"
                  >
                    Commercial
                  </Link>
                  <Link
                    href="/affordable-housing"
                    className="hover:text-white transition-colors"
                  >
                    Affordable Housing
                  </Link>
                </nav>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-white font-bold text-lg mb-6">
                  Quick Links
                </h3>
                <nav
                  className="flex flex-col space-y-3"
                  aria-label="Quick links"
                >
                  <Link
                    href="/browse"
                    className="hover:text-white transition-colors"
                  >
                    Browse Properties
                  </Link>
                  <Link
                    href="/post-property"
                    className="hover:text-white transition-colors"
                  >
                    List Your Property
                  </Link>
                  <Link
                    href="/register"
                    className="hover:text-white transition-colors"
                  >
                    Create Account
                  </Link>
                  <Link
                    href="/login"
                    className="hover:text-white transition-colors"
                  >
                    Sign In
                  </Link>
                </nav>
              </div>
            </div>

            <div className="border-t border-gray-800 dark:border-gray-700 pt-8 text-center">
              <p className="text-gray-400 dark:text-gray-500">
                &copy; 2025 PH Property Finder. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </Suspense>
    </div>
  )
}
